import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Play, Square, Pencil, XCircle, MessageSquare, Radio } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";
import { StatusBadge } from "@/components/LiveBadge";
import { fmtDateTime } from "@/lib/time";

export default function AdminWebinars() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);

  const load = () => api.get("/admin/webinars").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const action = async (id, verb) => {
    try {
      await api.post(`/admin/webinars/${id}/${verb}`);
      toast.success(`Webinar ${verb === "start" ? "is now live" : verb === "end" ? "ended" : "cancelled"}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-6xl">
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="overline mb-2">Broadcasts</div>
            <h1 className="font-display font-black text-4xl tracking-tight">Webinars</h1>
          </div>
          <button onClick={() => nav("/admin/webinars/new")} data-testid="create-webinar-btn"
            className="flex items-center gap-2 bg-[#0090A8] text-black hover:bg-[#00728A] font-bold rounded-sm px-4 py-2 text-sm transition-colors">
            <Plus size={16} /> Create Webinar
          </button>
        </div>

        {rows.length === 0 ? (
          <div className="border border-dashed border-[#E4E4E7] rounded-sm p-12 text-center text-gray-600">
            <Radio className="mx-auto mb-3 text-gray-700" size={28} />
            No webinars yet. Create one to auto-provision live stream settings.
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((w) => (
              <div key={w.id} data-testid={`admin-webinar-${w.id}`} className="bg-[#F5F5F5] border border-[#E4E4E7] rounded-sm p-5 flex items-center justify-between flex-wrap gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <StatusBadge status={w.status} />
                    <h3 className="font-display font-bold text-lg truncate">{w.title}</h3>
                  </div>
                  <div className="text-sm text-gray-500">{w.presenter_name || "TBA"} · {fmtDateTime(w.start_datetime)} · {w.duration_minutes} min</div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {w.status === "live" && (
                    <button onClick={() => nav(`/admin/webinars/${w.id}/qa`)} data-testid={`qa-monitor-${w.id}`} className="flex items-center gap-1.5 text-sm border border-[#0090A8]/40 text-[#0090A8] hover:bg-[#0090A8]/10 rounded-sm px-3 py-1.5 transition-colors">
                      <MessageSquare size={15} /> Q&A
                    </button>
                  )}
                  {w.status !== "live" && (
                    <button onClick={() => nav(`/admin/webinars/${w.id}/qa`)} data-testid={`qa-monitor-${w.id}`} className="flex items-center gap-1.5 text-sm border border-[#E4E4E7] text-gray-400 hover:text-[#0A0A0A] rounded-sm px-3 py-1.5 transition-colors">
                      <MessageSquare size={15} /> Q&A
                    </button>
                  )}
                  {(w.status === "upcoming" || w.status === "ended") && (
                    <button onClick={() => action(w.id, "start")} data-testid={`start-${w.id}`} className="flex items-center gap-1.5 text-sm border border-[#12805C]/40 text-[#12805C] hover:bg-[#12805C]/10 rounded-sm px-3 py-1.5 transition-colors">
                      <Play size={15} /> Go Live
                    </button>
                  )}
                  {w.status === "live" && (
                    <button onClick={() => action(w.id, "end")} data-testid={`end-${w.id}`} className="flex items-center gap-1.5 text-sm border border-[#D92D20]/40 text-[#D92D20] hover:bg-[#D92D20]/10 rounded-sm px-3 py-1.5 transition-colors">
                      <Square size={15} /> End
                    </button>
                  )}
                  <button onClick={() => nav(`/admin/webinars/${w.id}/edit`)} data-testid={`edit-${w.id}`} className="flex items-center gap-1.5 text-sm border border-[#E4E4E7] text-gray-400 hover:text-[#0A0A0A] rounded-sm px-3 py-1.5 transition-colors">
                    <Pencil size={15} /> Edit
                  </button>
                  {w.status !== "cancelled" && (
                    <button onClick={() => action(w.id, "cancel")} data-testid={`cancel-${w.id}`} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#D92D20] px-2 py-1.5 transition-colors">
                      <XCircle size={15} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
